import { useState } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function PresalesRequestModal({ opportunity, onClose, onSuccess }) {
  const [form, setForm] = useState({
    request_type: 'demo',
    title: `${opportunity?.name || 'Opportunity'} — Demo request`,
    requirements: '',
    urgency: 'medium',
    scheduled_date: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    // We now always send opportunity_id too — the backend will fall back to
    // it if the lead has been deleted. Only block submission if we have
    // neither (shouldn't happen, but belt-and-braces).
    if (!opportunity?.id) {
      setError('Opportunity context is missing. Please reload the page.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/presales`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          lead_id: opportunity.lead_id || null,
          opportunity_id: opportunity.id,
          request_type: form.request_type,
          title: form.title.trim(),
          requirements: form.requirements.trim(),
          urgency: form.urgency,
          scheduled_date: form.scheduled_date || null,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        // Robust error extraction: handles plain strings, validation-error
        // arrays from FastAPI, and dict-shaped details.
        const detail = data?.detail;
        let msg;
        if (typeof detail === 'string') {
          msg = detail;
        } else if (Array.isArray(detail)) {
          msg = detail.map(d => d.msg || d.message || JSON.stringify(d)).join('; ');
        } else if (detail && typeof detail === 'object') {
          msg = detail.message || JSON.stringify(detail);
        } else {
          msg = `Failed to create pre-sales request (HTTP ${response.status})`;
        }
        setError(msg);
        return;
      }
      const created = data?.request || data;
      onSuccess(created);
    } catch (err) {
      setError(err.message || 'Failed to create pre-sales request');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="presales-request-modal">
      <form onSubmit={handleSubmit} className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-lg">
        <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-slate-900 dark:text-white">Request Pre-Sales Help</h2>
            <p className="text-xs text-slate-500 mt-0.5">{opportunity.name}</p>
          </div>
          <button type="button" onClick={onClose} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg" aria-label="Close">
            <svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
        <div className="px-6 py-5 space-y-4">
          {error && <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-600 text-sm" data-testid="presales-modal-error">{error}</div>}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Request Type *</label>
              <select required value={form.request_type} onChange={e => setForm({ ...form, request_type: e.target.value })} className="w-full px-3 py-2 text-sm bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white" data-testid="presales-type">
                <option value="demo">Product Demo</option>
                <option value="poc">Proof of Concept</option>
                <option value="technical_evaluation">Technical Evaluation</option>
                <option value="solution_design">Solution Design</option>
                <option value="integration_assessment">Integration Assessment</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Urgency</label>
              <select value={form.urgency} onChange={e => setForm({ ...form, urgency: e.target.value })} className="w-full px-3 py-2 text-sm bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white" data-testid="presales-urgency">
                <option value="low">Low (24h SLA)</option>
                <option value="medium">Medium (8h SLA)</option>
                <option value="high">High (4h SLA)</option>
                <option value="critical">Critical (2h SLA)</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Title *</label>
            <input type="text" required value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="w-full px-3 py-2 text-sm bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white" data-testid="presales-title" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Requirements / context</label>
            <textarea rows={4} value={form.requirements} onChange={e => setForm({ ...form, requirements: e.target.value })} placeholder="What the customer asked for, tech stack, edge cases, attendees…" className="w-full px-3 py-2 text-sm bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white" data-testid="presales-requirements" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Scheduled for (optional)</label>
            <input type="datetime-local" value={form.scheduled_date} onChange={e => setForm({ ...form, scheduled_date: e.target.value })} className="w-full px-3 py-2 text-sm bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white" data-testid="presales-scheduled" />
          </div>
        </div>
        <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-2 bg-slate-50 dark:bg-slate-900/40 rounded-b-xl">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg">Cancel</button>
          <button type="submit" disabled={saving} className="px-4 py-2 text-sm font-semibold bg-amber-600 hover:bg-amber-700 text-white rounded-lg disabled:opacity-50" data-testid="presales-submit">
            {saving ? 'Creating…' : 'Create Request'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default PresalesRequestModal;
